import { useTranslations } from '@/i18n/use-translations';
import { Autocomplete, Box, Typography, Stack, Divider, TextField } from '@mui/material';
import { timeZoneOptions } from '@/utils/time-zone';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { updateEventSchemaFactory } from '@/schemas/event/update-event';
import type { z } from 'zod';
import TimePicker from '@/components/common/time-picker';
import { useEffect } from 'react';

interface DayConfig {
  startOffsetMinutes: number;
  openOffsetMinutes: number;
  endOffsetMinutes: number;
}

export default function ScheduleForm({
  form,
  initialDayIndex,
}: {
  form: UseFormReturn<z.infer<ReturnType<typeof updateEventSchemaFactory>>>;
  initialDayIndex?: number;
}) {
  const t = useTranslations();

  // Auto-scroll to specific day when initialDayIndex is provided
  useEffect(() => {
    if (initialDayIndex !== undefined) {
      setTimeout(() => {
        const dayElement = document.querySelector(`[data-day-index="${initialDayIndex}"]`);
        if (dayElement) {
          dayElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [initialDayIndex]);

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pr: 1 }}>
      <Typography variant="h6">{t('event.detail.settingsMenu.schedule.title')}</Typography>
      <Controller
        name="timezone"
        control={form.control}
        render={({ field: { value, onChange } }) => (
          <Autocomplete
            options={timeZoneOptions(value)}
            value={value || null}
            onChange={(_event, next) => {
              if (next) onChange(next);
            }}
            disableClearable={Boolean(value)}
            size="small"
            sx={{ mb: 1 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('event.detail.settingsMenu.schedule.timeZone')}
                helperText={t('event.detail.settingsMenu.schedule.timeZoneHint')}
              />
            )}
          />
        )}
      />
      <Controller
        name="days"
        control={form.control}
        render={({ field }) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {field.value?.map((_: DayConfig, index: number) => {
              const startDate = form.getValues('startDate');
              const dayDate = startDate
                ? new Date(new Date(startDate).setDate(new Date(startDate).getDate() + index))
                : null;

              return (
                <Box key={index} data-day-index={index}>
                  {index > 0 && <Divider sx={{ my: 1 }} />}
                  <Stack spacing={1}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                      {t('common.day')} {index + 1}
                      {dayDate && ` (${formatDate(dayDate)})`}
                    </Typography>

                    <Stack
                      direction="row"
                      spacing={1.5}
                      sx={{
                        '& > *': {
                          flex: 1,
                          minWidth: 0,
                          maxWidth: '100%',
                        },
                      }}
                    >
                      <Controller
                        name={`days.${index}.openOffsetMinutes`}
                        control={form.control}
                        render={({ field: { value, onChange } }) => (
                          <TimePicker
                            label={t('event.detail.settingsMenu.general.openTime')}
                            value={
                              value
                                ? new Date(2024, 0, 1, Math.floor(value / 60), value % 60)
                                : new Date(2024, 0, 1, 0, 0)
                            }
                            onChange={(newValue) => {
                              if (!newValue) return;
                              const minutes = newValue.getHours() * 60 + newValue.getMinutes();
                              onChange(minutes);
                            }}
                            slotProps={{
                              textField: {
                                variant: 'outlined',
                                size: 'small',
                                fullWidth: true,
                              },
                            }}
                          />
                        )}
                      />

                      <Controller
                        name={`days.${index}.startOffsetMinutes`}
                        control={form.control}
                        render={({ field: { value, onChange } }) => (
                          <TimePicker
                            label={t('event.detail.settingsMenu.general.startTime')}
                            value={
                              value
                                ? new Date(2024, 0, 1, Math.floor(value / 60), value % 60)
                                : new Date(2024, 0, 1, 0, 0)
                            }
                            onChange={(newValue) => {
                              if (!newValue) return;
                              const minutes = newValue.getHours() * 60 + newValue.getMinutes();
                              onChange(minutes);
                            }}
                            slotProps={{
                              textField: {
                                variant: 'outlined',
                                size: 'small',
                                fullWidth: true,
                              },
                            }}
                          />
                        )}
                      />

                      <Controller
                        name={`days.${index}.endOffsetMinutes`}
                        control={form.control}
                        render={({ field: { value, onChange } }) => (
                          <TimePicker
                            label={t('event.detail.settingsMenu.general.endTime')}
                            value={
                              value
                                ? new Date(2024, 0, 1, Math.floor(value / 60), value % 60)
                                : new Date(2024, 0, 1, 0, 0)
                            }
                            onChange={(newValue) => {
                              if (!newValue) return;
                              const minutes = newValue.getHours() * 60 + newValue.getMinutes();
                              onChange(minutes);
                            }}
                            slotProps={{
                              textField: {
                                variant: 'outlined',
                                size: 'small',
                                fullWidth: true,
                              },
                            }}
                          />
                        )}
                      />
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
          </Box>
        )}
      />
    </Box>
  );
}
